import { useEffect, useRef, useState } from "react";
import { Link } from "@/lib/router-compat";
import PageMeta from "@/components/PageMeta";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { AlertTriangle, FileText, PhoneCall, ShieldCheck } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import DpaPayoffVsSubordinateCalculator from "@/components/tools/DpaPayoffVsSubordinateCalculator";
import RelatedTools from "@/components/tools/RelatedTools";
import {
  COMPARISON_ROWS,
  DPA_FAQS,
  DPA_TYPES,
  FORGIVENESS_TIMELINE,
  GATHER_CHECKLIST,
  REFI_SCENARIOS,
  SELL_SCENARIOS,
  type ScenarioBlock,
} from "@/data/dpaSellRefinanceData";

const CONTENTS = [
  { id: "dpa-type", label: "Start here: know your DPA type" },
  { id: "sell-early", label: "If you sell early" },
  { id: "refinance", label: "If you refinance" },
  { id: "subordinate", label: "Pay off or subordinate?" },
  { id: "recapture", label: "Recapture tax" },
  { id: "compare", label: "Sell vs refinance at a glance" },
  { id: "timeline", label: "How forgiveness burns off" },
  { id: "gather", label: "What to gather before you call" },
  { id: "next-steps", label: "Your next step" },
  { id: "faq", label: "Common questions" },
];

const CTAS = [
  {
    id: "payoff_quote",
    icon: PhoneCall,
    title: "Request a payoff quote",
    body:
      "Call the servicer named on your second-lien note and ask for a written payoff statement with a good-through date. It is the only number your closing agent can use.",
  },
  {
    id: "subordination_guidelines",
    icon: ShieldCheck,
    title: "Ask for subordination guidelines",
    body:
      "Before you apply to refinance, request the program's written subordination policy: eligible refinance types, combined loan-to-value cap, processing fee, and turn time.",
  },
  {
    id: "review_second_lien",
    icon: FileText,
    title: "Review your second lien documents",
    body:
      "Pull your promissory note and recorded deed of trust from your closing package. Forgiveness schedule, interest terms, and triggers all live there — not in the brochure.",
  },
];

function ScenarioGrid({ blocks, section }: { blocks: ScenarioBlock[]; section: string }) {
  return (
    <div className="space-y-4">
      {blocks.map((b) => (
        <Card key={b.id} id={b.id} className="border-border scroll-mt-24">
          <CardContent className="p-5">
            <p className="font-semibold text-foreground mb-3">{b.type}</p>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="font-semibold text-foreground">Payoff timing</dt>
                <dd className="text-muted-foreground leading-relaxed">{b.payoffTiming}</dd>
              </div>
              <div>
                <dt className="font-semibold text-foreground">What the balance is</dt>
                <dd className="text-muted-foreground leading-relaxed">{b.balanceLogic}</dd>
              </div>
              <div>
                <dt className="font-semibold text-foreground">At the closing table</dt>
                <dd className="text-muted-foreground leading-relaxed">{b.closingImpact}</dd>
              </div>
            </dl>
            <p className="sr-only">{section}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function CtaBlock({ sectionName }: { sectionName: string }) {
  return (
    <div className="grid gap-3 sm:grid-cols-3 mt-6">
      {CTAS.map((cta) => {
        const Icon = cta.icon;
        return (
          <Card
            key={cta.id}
            className="border-primary/30 bg-primary/5 cursor-pointer transition-colors hover:bg-primary/10"
            role="button"
            tabIndex={0}
            onClick={() =>
              trackEvent("dpa_cta_click", { section_name: sectionName, cta_type: cta.id })
            }
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                trackEvent("dpa_cta_click", { section_name: sectionName, cta_type: cta.id });
              }
            }}
          >
            <CardContent className="p-4">
              <Icon className="h-5 w-5 text-primary mb-2" aria-hidden />
              <p className="font-semibold text-foreground text-sm mb-1">{cta.title}</p>
              <p className="text-xs text-muted-foreground leading-relaxed">{cta.body}</p>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

const DpaSellOrRefinance = () => {
  const [openFaq, setOpenFaq] = useState<string>("");
  const seen = useRef<Set<string>>(new Set());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id;
          if (!entry.isIntersecting || seen.current.has(id)) return;
          seen.current.add(id);
          trackEvent("dpa_guide_section_view", { section_name: id });
        });
      },
      { threshold: 0.35 }
    );
    CONTENTS.forEach((c) => {
      const el = document.getElementById(c.id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleFaqChange = (value: string) => {
    setOpenFaq(value);
    if (value) {
      const faq = DPA_FAQS.find((f) => f.id === value);
      trackEvent("dpa_faq_expand", { question_id: value, topic: faq?.topic ?? "unknown" });
    }
  };



  return (
    <div className="min-h-screen bg-background flex flex-col">
      <PageMeta
        title="DPA if You Sell or Refinance Early — What You Owe"
        description="Selling or refinancing with down payment assistance? See payoff timing by DPA type, how unforgiven balances are calculated, when subordination works, and how recapture tax fits in."
        path="/tools/dpa-sell-or-refinance"
      />
      <Header />

      <main className="flex-1">
        <div className="container mx-auto px-4 py-8 sm:py-12 max-w-4xl">
          <Card className="mb-6 border-primary/30 bg-primary/5">
            <CardContent className="p-5 sm:p-6">
              <p className="text-xs font-bold uppercase tracking-wide text-primary mb-2">
                Short answer
              </p>
              <p className="text-base sm:text-lg text-foreground leading-relaxed">
                It depends on what your assistance actually is. A <strong>true grant</strong> usually
                costs nothing. A <strong>forgivable loan</strong> charges only the portion that has not
                been forgiven yet — unless it is all-or-nothing. A <strong>deferred-payment loan</strong>{" "}
                comes due in full. An <strong>amortizing second</strong> is paid off like any mortgage.
                Selling almost always clears the lien; refinancing may not, if the program agrees to{" "}
                <strong>subordinate</strong>. Terms vary by program.
              </p>
            </CardContent>
          </Card>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground mb-4">
            What Happens to Your Down Payment Assistance if You Sell or Refinance Early?
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground mb-6">
            Down payment assistance is easy to accept and hard to unwind. Years later, when a job moves
            you or rates drop far enough to refinance, the same help that got you in the door becomes a
            line item on a settlement statement. This guide walks through what happens to that money in
            both directions — selling early and refinancing — so you can price the decision before you
            make it.
          </p>

          <nav aria-label="On this page" className="mb-10 rounded-xl border border-border bg-muted/40 p-5">
            <p className="text-sm font-bold text-foreground mb-3">On this page</p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {CONTENTS.map((c) => (
                <li key={c.id}>
                  <a href={`#${c.id}`} className="text-primary hover:underline">
                    {c.label}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* DPA type */}
          <section id="dpa-type" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-3">
              Start here: know your DPA type
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-5">
              Every answer below depends on this one question, and most homeowners have to look it up.
              Your purchase closing package holds the answer — specifically whether there is a second
              promissory note, whether a lien was recorded, and what the note says about forgiveness.
            </p>
            <div className="grid gap-3 sm:grid-cols-2">
              {DPA_TYPES.map((t) => (
                <Card key={t.id} className="border-border">
                  <CardContent className="p-5">
                    <p className="font-semibold text-foreground">{t.label}</p>
                    <p className="text-sm text-primary mb-2">{t.short}</p>
                    <p className="text-sm text-muted-foreground leading-relaxed">{t.identify}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          {/* Sell early */}
          <section id="sell-early" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-3">If you sell early</h2>
            <p className="text-muted-foreground leading-relaxed mb-5">
              Selling ends your ownership, so any recorded lien has to be cleared before title can
              transfer. The question is never whether the lien gets addressed — it is how much comes out
              of your proceeds.
            </p>
            <ScenarioGrid blocks={SELL_SCENARIOS} section="sell" />

            <Card className="mt-5 border-l-4 border-l-primary bg-muted/40">
              <CardContent className="p-4 flex gap-3">
                <AlertTriangle className="h-5 w-5 text-primary shrink-0 mt-0.5" aria-hidden />
                <p className="text-sm text-muted-foreground leading-relaxed">
                  <strong className="text-foreground">Order the payoff early.</strong> Programs commonly
                  need one to three weeks to issue a written payoff statement, and the quote expires. Ask
                  for it the day you go under contract, and give a copy to your closing agent.
                </p>
              </CardContent>
            </Card>
          </section>

          {/* Refinance */}
          <section id="refinance" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-3">If you refinance</h2>
            <p className="text-muted-foreground leading-relaxed mb-5">
              Refinancing pays off your first mortgage and records a new one. Without action, your
              assistance lien would move up into first position, which no first-mortgage lender accepts.
              That is why every refinance with DPA runs into the same fork: subordinate the lien, or pay
              it off.
            </p>
            <ScenarioGrid blocks={REFI_SCENARIOS} section="refinance" />
            <CtaBlock sectionName="refinance" />
          </section>

          {/* Subordinate */}
          <section id="subordinate" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-3">Pay off or subordinate?</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                <strong className="text-foreground">Subordination</strong> is the program formally
                agreeing to stay in second position behind your new first mortgage. It is a document
                they sign and re-record; it does not change what you owe them. When it is granted, a
                forgiveness clock usually keeps running as if nothing happened — but confirm that in
                writing, because a minority of programs restart or terminate it.
              </p>
              <p>
                <strong className="text-foreground">Cash-out is the usual dividing line.</strong> Many
                agencies subordinate freely for a rate-and-term refinance and decline for cash-out,
                since pulling equity out shrinks the cushion protecting their position. Where limited
                cash out is allowed, it is often restricted to documented home repairs and capped by a
                combined loan-to-value limit.
              </p>
              <p>
                <strong className="text-foreground">Net tangible benefit</strong> tests show up too. Some
                programs will only subordinate if the refinance clearly helps you — a lower rate, a
                lower payment, or a move out of an adjustable loan. A refinance that mainly generates
                fees can be declined on that basis alone.
              </p>
              <p>
                <strong className="text-foreground">Expect a fee and a queue.</strong> Subordination
                processing fees are common, and review turn times run from a few days to several weeks.
                Start the request before you apply, not after you lock a rate.
              </p>
              <p>
                <strong className="text-foreground">If the request is denied,</strong> the refinance can
                still close — but only by paying the assistance off at closing. Model that payoff into
                your break-even before you decide. Sometimes the honest answer is to keep the loan you
                have. Terms vary by program.
              </p>
            </div>
            <div className="mt-6">
              <DpaPayoffVsSubordinateCalculator />
            </div>
            <CtaBlock sectionName="subordinate" />
          </section>

          {/* Recapture */}
          <section id="recapture" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-3">Recapture tax</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                Recapture tax is separate from repaying your assistance. It is a federal tax that can
                apply to mortgages financed through mortgage revenue bonds, and to some
                mortgage-credit-certificate loans, when three things line up at the same time: you sell
                within roughly the first nine years, you sell at a gain, and your household income at
                the time of sale exceeds the program's adjusted limit.
              </p>
              <p>
                All three tests generally have to be met. Many homeowners who technically fall inside
                the window owe nothing, because there was no gain, or income stayed under the limit, or
                the calculated amount is capped at a share of the gain. The amount also typically phases
                down after the midpoint of the period.
              </p>
              <p>
                Refinancing by itself is usually not a recapture event, because you have not sold or
                disposed of the home — but the recapture window generally keeps running against the
                original purchase date rather than restarting.
              </p>
              <p>
                Check your closing package for a recapture notice. If one exists, keep it — the numbers
                on it feed the calculation years later. Recapture rules are federal tax rules and change
                over time; confirm your situation with a tax professional and your housing agency before
                relying on any expected outcome.
              </p>
            </div>
            <CtaBlock sectionName="recapture" />
          </section>

          {/* Comparison table */}
          <section id="compare" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-4">
              Sell vs refinance at a glance
            </h2>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full text-sm">
                <caption className="sr-only">
                  How down payment assistance behaves when selling compared with refinancing
                </caption>
                <thead className="bg-muted/60">
                  <tr>
                    <th scope="col" className="text-left font-semibold text-foreground p-3">
                      Question
                    </th>
                    <th scope="col" className="text-left font-semibold text-foreground p-3">
                      Selling
                    </th>
                    <th scope="col" className="text-left font-semibold text-foreground p-3">
                      Refinancing
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row) => (
                    <tr key={row.question} className="border-t border-border align-top">
                      <th scope="row" className="text-left font-semibold text-foreground p-3">
                        {row.question}
                      </th>
                      <td className="p-3 text-muted-foreground leading-relaxed">{row.sell}</td>
                      <td className="p-3 text-muted-foreground leading-relaxed">{row.refi}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              General patterns only. Terms vary by program, state, and funding source.
            </p>
          </section>

          {/* Timeline */}
          <section id="timeline" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-1">How forgiveness burns off</h2>
            <p className="text-sm text-muted-foreground mb-5">
              An illustration of a five-year straight-line forgivable loan — the share still owed if you
              sell or are required to pay off during each year. Your program's period may be five, ten,
              or fifteen years, and all-or-nothing programs stay at 100% until the very end.
            </p>
            <div className="space-y-2">
              {FORGIVENESS_TIMELINE.map((t) => (
                <div key={t.year} className="flex items-center gap-3">
                  <span className="w-16 shrink-0 text-xs font-semibold text-foreground">{t.year}</span>
                  <div className="flex-1 h-6 rounded bg-muted overflow-hidden">
                    <div
                      className="h-full bg-primary/70"
                      style={{ width: `${t.remaining}%` }}
                      aria-hidden
                    />
                  </div>
                  <span className="w-20 shrink-0 text-xs text-muted-foreground text-right">
                    {t.remaining}% owed
                  </span>
                </div>
              ))}
            </div>
          </section>

          {/* Gather */}
          <section id="gather" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-1">
              What to gather before you call
            </h2>
            <p className="text-sm text-muted-foreground mb-5">
              Having these in hand turns a two-week back-and-forth into one phone call.
            </p>
            <Card className="border-border">
              <CardContent className="p-5">
                <ul className="space-y-3 text-sm text-muted-foreground leading-relaxed list-disc pl-5">
                  {GATHER_CHECKLIST.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>

          {/* Next steps */}
          <section id="next-steps" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-1">Your next step</h2>
            <p className="text-sm text-muted-foreground">
              Pick the one that matches where you are today.
            </p>
            <CtaBlock sectionName="next_steps" />
          </section>

          {/* FAQ */}
          <section id="faq" className="mb-12 scroll-mt-24">
            <h2 className="text-2xl font-bold text-foreground mb-4">Common questions</h2>
            <Accordion
              type="single"
              collapsible
              value={openFaq}
              onValueChange={handleFaqChange}
              className="rounded-xl border border-border bg-card px-4 [&_[role=region][data-state=closed]]:hidden"
            >
              {DPA_FAQS.map((faq) => (
                <AccordionItem key={faq.id} value={faq.id}>
                  <AccordionTrigger className="text-left text-base font-semibold">
                    {faq.q}
                  </AccordionTrigger>
                  <AccordionContent
                    forceMount
                    className="text-sm text-muted-foreground leading-relaxed"
                  >
                    {faq.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>

          <section className="mb-6">
            <h2 className="text-lg font-bold text-foreground mb-3">Keep going</h2>
            <div className="flex flex-wrap gap-3 text-sm">
              <Link to="/tools/repayment-triggers" className="text-primary hover:underline">
                Repayment triggers before accepting assistance
              </Link>
              <span className="text-muted-foreground">·</span>
              <Link to="/tools/dpa-hidden-strings" className="text-primary hover:underline">
                Hidden strings attached to "free" down payment money
              </Link>
              <span className="text-muted-foreground">·</span>
              <Link to="/tools/combining-assistance-programs" className="text-primary hover:underline">
                What stops two assistance programs from combining
              </Link>
              <span className="text-muted-foreground">·</span>
              <Link to="/tools/low-income-first-time-buyer-grants" className="text-primary hover:underline">
                Grants for low-income first-time buyers
              </Link>
              <span className="text-muted-foreground">·</span>
              <Link to="/tools/high-income-down-payment-help" className="text-primary hover:underline">
                Down payment help for high-income buyers
              </Link>
              <span className="text-muted-foreground">·</span>
              <Link to="/stack" className="text-primary hover:underline">
                Stack down payment assistance
              </Link>
            </div>
          </section>

          <p className="text-xs text-muted-foreground">
            Educational information only — not legal, financial, or tax advice. Repayment, forgiveness,
            subordination, and recapture terms vary by program, state, agency, and funding source, and
            they change over time. Confirm every detail with your housing agency, your loan servicer,
            and your own closing documents before acting.
          </p>
        </div>
      </main>

      <RelatedTools currentPath="/tools/dpa-sell-or-refinance" />
      <Footer />
    </div>
  );
};

export default DpaSellOrRefinance;
